import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import type { RootState } from "@/store/store";
import { parseURLParams } from "@/utils/url";
import {
  BRANDS,
  FILTERS,
  MARKETS,
  POINT_IN_TIME,
  PROFILE,
  TREND,
  SECONDARY_BRANDS,
  URL_BY_BRAND,
  URL_BY_MARKET,
  URL_BY_SEGMENT,
} from "@/constant/constantsThrougoutProject";
import type {
  CurrentScreenContext,
  FilterRef,
  BrandRef,
  MarketRef,
} from "../types/screenContext";
import { DEFAULT_SCREEN_CONTEXT } from "../types/screenContext";
import { selectLegacyFilterOptionIdArray } from "@/store/slices/userSelection/selectors";

const SCREEN_TYPE_TO_GROUPED_BY: Record<string, string> = {
  [URL_BY_SEGMENT]: "SEGMENT",
  [URL_BY_BRAND]: "BRAND",
  [URL_BY_MARKET]: "MARKET",
};

const buildPeriodRef = (
  type: string | null | undefined,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
) => {
  const nextType = type || "";
  return {
    type: nextType,
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  };
};

/**
 * Builds the v2 `currentScreenContext` payload from Redux store state.
 *
 * Returns a stable `buildCurrentScreenContext()` function that produces the
 * payload expected by `/ai-assistant/usermessage`.
 *
 * `groupedBy` is derived from `userSelection.screenType` so it tracks the
 * active By * tab from app state instead of inferring it from the URL.
 */
export function useScreenContext() {
  const location = useLocation();

  const userSelection = useSelector(
    (state: RootState) => state.userSelection,
  );
  // Legacy consumer of the Filters+Segments V2 migration's shim:
  // `userSelection.filters` transitionally carries `FilterSelection[]`;
  // flatten it back to the flat option-id `number[]` this screen-context
  // builder still expects, via the catalog code->id lookup.
  const legacyFilterIds = useSelector(selectLegacyFilterOptionIdArray);
  const brandEntities = useSelector(
    (state: RootState) => state.activeGroup.brands.entities,
  );
  const marketEntities = useSelector(
    (state: RootState) => state.activeGroup.markets.markets.entities,
  );
  const filterEntities = useSelector(
    (state: RootState) => state.activeGroup.filters.filters.entities,
  );

  const buildCurrentScreenContext = useCallback((): CurrentScreenContext => {
    const pathSegments = (location?.pathname || "")
      .split("/")
      .filter(Boolean);
    const module = pathSegments[0] || "";
    const screenTypeKey = userSelection.screenType?.toLowerCase();
    const groupedByFromScreenType = screenTypeKey
      ? SCREEN_TYPE_TO_GROUPED_BY[screenTypeKey]
      : undefined;

    const rawParams = parseURLParams(
      new URLSearchParams(location?.search || ""),
    );

    // --- brands: [{id, name}] ---
    let brandIds: (number | string)[] = [...(userSelection.brands ?? [])];
    if (brandIds.length === 0) {
      const fromUrl = rawParams[BRANDS];
      if (Array.isArray(fromUrl)) {
        brandIds = fromUrl as (number | string)[];
      }
    }
    const brands: BrandRef[] = brandIds
      .map((id: number | string) => {
        const entity = brandEntities[id as any];
        return entity
          ? { id: entity.id, name: entity.displayName || entity.name }
          : null;
      })
      .filter(Boolean) as BrandRef[];

    // --- secondaryBrands: [{id, name}] ---
    let secondaryBrandIds: (number | string)[] = [...(userSelection.secondaryBrands ?? [])];
    if (secondaryBrandIds.length === 0) {
      const fromUrl = rawParams[SECONDARY_BRANDS];
      if (Array.isArray(fromUrl)) {
        secondaryBrandIds = fromUrl as (number | string)[];
      }
    }
    const secondaryBrands: BrandRef[] = secondaryBrandIds
      .map((id: number | string) => {
        const entity = brandEntities[id as any];
        return entity
          ? { id: entity.id, name: entity.displayName || entity.name }
          : null;
      })
      .filter(Boolean) as BrandRef[];

    // --- markets: [{id, name}] ---
    let marketIds = [...(userSelection.markets ?? [])];
    if (marketIds.length === 0) {
      const fromUrl = rawParams[MARKETS];
      if (Array.isArray(fromUrl)) {
        marketIds = fromUrl as number[];
      }
    }
    const markets: MarketRef[] = marketIds
      .map((id: number) => {
        const entity = marketEntities[id];
        return entity ? { id: entity.id, name: entity.name } : null;
      })
      .filter(Boolean) as MarketRef[];

    const viewMode = userSelection.mode || DEFAULT_SCREEN_CONTEXT.viewMode;
    const visualizationType =
      userSelection.visualizationType ||
      DEFAULT_SCREEN_CONTEXT.visualizationType;
    const normalizedViewMode = String(viewMode).toUpperCase();
    const normalizedVisualizationType = String(visualizationType).toUpperCase();

    // --- metric/group/response keys ---
    const metricKeys: Array<string | number> = [];
    const groupKeys: Array<string | number> = [];
    const responseKeys: Array<string | number> = [];

    if (normalizedViewMode === TREND) {
      if (userSelection.trendsMetric) {
        metricKeys.push(userSelection.trendsMetric);
      }
      if (userSelection.trendsResponse) {
        responseKeys.push(userSelection.trendsResponse);
      }
    } else if (
      normalizedViewMode === POINT_IN_TIME &&
      normalizedVisualizationType === PROFILE
    ) {
      if (userSelection.profileGroup != null) {
        groupKeys.push(userSelection.profileGroup);
      }
      if (userSelection.profileMetric != null) {
        metricKeys.push(userSelection.profileMetric);
      }
      if (userSelection.profileResponse != null) {
        responseKeys.push(userSelection.profileResponse);
      }
    }

    // --- filters: [{type, filterId, filterOptions}] ---
    const selectedIdsSource =
      legacyFilterIds && legacyFilterIds.length > 0
        ? legacyFilterIds
        : (rawParams[FILTERS] as number[] | undefined) ?? [];
    const selectedOptionIds = new Set(selectedIdsSource ?? []);
    const filters: FilterRef[] = [];

    if (selectedOptionIds.size > 0 && filterEntities) {
      const filterIds = Object.keys(filterEntities);
      for (const fid of filterIds) {
        const filter = filterEntities[Number(fid)];
        if (!filter?.options) continue;

        const matchingOptions = filter.options
          .filter((opt) => selectedOptionIds.has(opt.id))
          .map((opt) => opt.id);

        if (matchingOptions.length > 0) {
          filters.push({
            type: filter.kind || "DEFAULT",
            filterId: filter.id,
            filterOptions: matchingOptions,
          });
        }
      }
    }

    const period = buildPeriodRef(
      userSelection.periodType,
      userSelection.periodStartDate,
      userSelection.periodEndDate,
    );
    const postPeriod = buildPeriodRef(
      userSelection.postPeriodType,
      userSelection.postStartDate,
      userSelection.postEndDate,
    );

    return {
      brands,
      markets,
      module,
      metricKeys,
      groupKeys,
      responseKeys,
      // Some screens store segment selection under `userSelection.activeSegments`
      // (e.g. segment selector routes). Prefer that when present.
      segments: userSelection.segments ?? [],
      filters,
      audienceBase: userSelection.base || DEFAULT_SCREEN_CONTEXT.audienceBase,
      viewMode,
      visualizationType,
      groupedBy:
        groupedByFromScreenType ??
        (userSelection.groupedBy || DEFAULT_SCREEN_CONTEXT.groupedBy),
      statTest: userSelection.statTest || DEFAULT_SCREEN_CONTEXT.statTest,
      scoreType: userSelection.scoreType || DEFAULT_SCREEN_CONTEXT.scoreType,
      intervalType: userSelection.period || DEFAULT_SCREEN_CONTEXT.intervalType,
      secondaryBrands,
      period,
      postPeriod,
    };
  }, [
    location?.pathname,
    location?.search,
    userSelection,
    legacyFilterIds,
    brandEntities,
    marketEntities,
    filterEntities,
  ]);

  return { buildCurrentScreenContext };
}
